/**
 * ============================================================================
 * 🔐 Google Auth Service — GIS (Web) + Capacitor Firebase Auth (Native)
 * ============================================================================
 * 
 * Plattform-übergreifende Google-Authentifizierung:
 * - Web: Google Identity Services (GIS) für One Tap / Button Login
 * - Android/iOS: @capacitor-firebase/authentication für nativen Login
 * 
 * Web Client ID: 370627289950-ri1m5aa0v96jjrkt6q3kcn5p4eppnb1d
 * ============================================================================
 */

import { tGlobal } from '../utils/tGlobal';

const WEB_CLIENT_ID = '370627289950-ri1m5aa0v96jjrkt6q3kcn5p4eppnb1d.apps.googleusercontent.com';

// Plattform-Erkennung
const isNativePlatform = () => {
  return typeof window !== 'undefined' && 
         window.Capacitor && 
         window.Capacitor.isNativePlatform && 
         window.Capacitor.isNativePlatform();
};

let _gisReady = false;
let _gisReadyPromise = null;

// ============================================================
// 🔧 GIS Library laden
// ============================================================

/**
 * Stellt sicher, dass die GIS-Library geladen ist
 */
function ensureGISLoaded() {
  if (_gisReady && window.google?.accounts) {
    return Promise.resolve();
  }

  if (_gisReadyPromise) return _gisReadyPromise;

  _gisReadyPromise = new Promise((resolve, reject) => {
    // Prüfe ob schon geladen
    if (window.google?.accounts) {
      _gisReady = true;
      resolve();
      return;
    }

    // Warte auf Script-Load (Script ist in index.html)
    const checkInterval = setInterval(() => {
      if (window.google?.accounts) {
        clearInterval(checkInterval);
        _gisReady = true;
        resolve();
      }
    }, 100);

    // Timeout nach 10 Sekunden
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.google?.accounts) {
        reject(new Error(tGlobal('ui.svc.gisLoadFailed')));
      }
    }, 10000);
  });

  return _gisReadyPromise;
}

// ============================================================
// 🔐 GOOGLE SIGN-IN (ID Token)
// ============================================================

/**
 * Google Sign-In — gibt ID Token zurück
 * 
 * Plattform-Unterscheidung:
 * - Native (Android/iOS): Capacitor Firebase Auth Plugin
 * - Web: GIS One Tap / Button
 * 
 * @returns {Promise<{idToken: string, accessToken: string|null}>}
 */
export function signInWithGoogle() {
  if (isNativePlatform()) {
    return signInWithGoogleNative();
  }
  return signInWithGoogleWeb();
}

/**
 * Native Google Sign-In via Capacitor Firebase Auth
 */
async function signInWithGoogleNative() {
  console.log('🔐 [Native] Starte Google-Login via Capacitor Firebase Auth...');
  
  try {
    // Dynamisch importieren um Web-Bundle nicht zu belasten
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
    
    let result;
    try {
      result = await FirebaseAuthentication.signInWithGoogle({
        scopes: ['profile', 'email'],
      });
    } catch (ersterFehler) {
      // 🔁 Fallback (Green Goat, 26.09.2026): Auf manchen Geräten scheitert der neue Android-
      // „Credential Manager" nach einem Abmelden still → Login tat scheinbar nichts. Dann den
      // klassischen Google-Login versuchen. Bewusster Abbruch durch den Nutzer → NICHT erneut.
      const m = String((ersterFehler && (ersterFehler.message || ersterFehler.code)) || ersterFehler || '');
      // Auch bei „abgebrochen" den klassischen Weg versuchen: Der Credential Manager meldet nach dem Google-Dialog
      // („Weiter") manchmal faelschlich einen Abbruch — die App tat dann nichts (Philipp, 12.10.2026: 9 Versuche,
      // keiner erreichte den Server). Bricht der Nutzer auch den klassischen Dialog ab, ist es ein echter Abbruch.
      console.warn('🔐 [Native] Credential Manager fehlgeschlagen → klassischer Google-Login:', m);
      try { const { reportError } = await import('./errorReport'); reportError('login_stufe1', `Credential Manager: ${m.slice(0, 160)}`, { stufe: 1 }); } catch { /* */ }
      result = await FirebaseAuthentication.signInWithGoogle({
        scopes: ['profile', 'email'],
        useCredentialManager: false,
      });
    }
    
    console.log('🔐 [Native] Google Login erfolgreich:', result.user?.email);
    
    // idToken aus dem Credential extrahieren
    const idToken = result.credential?.idToken;
    const accessToken = result.credential?.accessToken || null;
    
    if (!idToken) {
      // Fallback: User ist eingeloggt, aber kein idToken im Credential
      // Versuche das idToken vom aktuellen User zu holen
      const tokenResult = await FirebaseAuthentication.getIdToken();
      if (tokenResult.token) {
        return {
          idToken: tokenResult.token,
          accessToken: accessToken,
        };
      }
      throw new Error('Kein ID Token erhalten');
    }
    
    return {
      idToken: idToken,
      accessToken: accessToken,
    };
  } catch (error) {
    console.error('🔐 [Native] Google Login fehlgeschlagen:', error);
    
    // Benutzerfreundliche Fehlermeldungen
    if (error.message?.includes('canceled') || error.code === 'auth/cancelled-popup-request') {
      throw new Error('Anmeldung abgebrochen');
    }
    if (error.message?.includes('network') || error.code === 'auth/network-request-failed') {
      throw new Error(tGlobal('ui.svc.networkError'));
    }
    if (error.code === 'auth/operation-not-allowed') {
      throw new Error(tGlobal('ui.svc.googleLoginNotConfigured'));
    }
    
    throw error;
  }
}

/**
 * Web Google Sign-In via GIS (Google Identity Services)
 */
function signInWithGoogleWeb() {
  return new Promise(async (resolve, reject) => {
    try {
      await ensureGISLoaded();
    } catch (err) {
      reject(err);
      return;
    }

    let resolved = false;

    // Callback für ID Token (One Tap oder Button)
    const handleCredential = (response) => {
      if (resolved) return;
      resolved = true;

      // Modal aufräumen falls vorhanden
      removeSignInModal();

      if (response.credential) {
        resolve({
          idToken: response.credential,
          accessToken: null, // Wird separat über requestDriveToken geholt
        });
      } else {
        reject(new Error('Kein Google Credential erhalten'));
      }
    };

    // GIS initialisieren
    window.google.accounts.id.initialize({
      client_id: WEB_CLIENT_ID,
      callback: handleCredential,
      auto_select: false,
      cancel_on_tap_outside: false,
    });

    // Versuch 1: One Tap / FedCM
    window.google.accounts.id.prompt((notification) => {
      if (resolved) return;

      const displayed = !(notification.isNotDisplayed() || notification.isSkippedMoment());

      if (!displayed) {
        console.log('🔐 One Tap nicht verfügbar → zeige Login-Modal');
        showSignInModal(handleCredential, () => {
          if (!resolved) {
            resolved = true;
            reject(new Error('Anmeldung abgebrochen'));
          }
        });
      }
    });
  });
}

/**
 * Zeigt ein Modal mit dem offiziellen Google Sign-In Button
 */
function showSignInModal(onCredential, onCancel) {
  // Altes Modal entfernen falls vorhanden
  removeSignInModal();

  const overlay = document.createElement('div');
  overlay.id = 'gis-signin-overlay';
  overlay.style.cssText = `
    position: fixed; inset: 0; 
    background: rgba(0,0,0,0.6); 
    display: flex; align-items: center; justify-content: center; 
    z-index: 999999; backdrop-filter: blur(4px);
  `;

  const card = document.createElement('div');
  card.style.cssText = `
    background: white; border-radius: 16px; padding: 32px; 
    text-align: center; max-width: 400px; width: 90%;
    box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  `;

  const title = document.createElement('h3');
  title.textContent = tGlobal('ui.svc.signInWithGoogle');
  title.style.cssText = 'margin: 0 0 8px; font-size: 20px; color: #333;';

  const subtitle = document.createElement('p');
  subtitle.textContent = tGlobal('ui.svc.signInSubtitle');
  subtitle.style.cssText = 'margin: 0 0 24px; color: #666; font-size: 14px;';

  const buttonContainer = document.createElement('div');
  buttonContainer.id = 'gis-signin-button';
  buttonContainer.style.cssText = 'display: flex; justify-content: center; min-height: 44px;';

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = tGlobal('ui.svc.cancel');
  cancelBtn.style.cssText = `
    margin-top: 16px; padding: 8px 24px; 
    background: none; border: 1px solid #ddd; border-radius: 8px; 
    color: #666; cursor: pointer; font-size: 14px;
  `;
  cancelBtn.onclick = () => {
    removeSignInModal();
    onCancel();
  };

  card.appendChild(title);
  card.appendChild(subtitle);
  card.appendChild(buttonContainer);
  card.appendChild(cancelBtn);
  overlay.appendChild(card);

  // Klick auf Overlay = Abbrechen
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      removeSignInModal();
      onCancel();
    }
  });

  document.body.appendChild(overlay);

  // Google Button rendern
  window.google.accounts.id.renderButton(buttonContainer, {
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
    shape: 'rectangular',
    width: 300,
    locale: 'de',
  });
}

/**
 * Entfernt das Sign-In Modal
 */
function removeSignInModal() {
  const overlay = document.getElementById('gis-signin-overlay');
  if (overlay) overlay.remove();
}

// ============================================================
// 🔑 DRIVE ACCESS TOKEN (für Google Drive API)
// ============================================================

// Mutable Refs für Drive-Token (nur noch für Native nötig)
let _driveResolve = null;
let _driveReject = null;

/**
 * Google Drive Access Token anfordern
 * 
 * Plattform-Unterscheidung:
 * - Native: Über Capacitor Firebase Auth (falls verfügbar)
 * - Web: google.accounts.oauth2 Token Client
 * 
 * @param {boolean} interactive - true = Consent-Popup erlaubt, false = silent
 * @returns {Promise<string>} Access Token
 */
export function requestDriveToken(interactive = true) {
  if (isNativePlatform()) {
    return requestDriveTokenNative(interactive);
  }
  return requestDriveTokenWeb(interactive);
}

/**
 * Native Drive Token via Capacitor
 * Strategie:
 *   1) Server-Token (nutzt gespeicherten Google Refresh Token — kein Login nötig)
 *   2) Re-Auth mit Drive-Scope → serverAuthCode an Server → Refresh Token speichern
 *      → ab jetzt dauerhaft verbunden, künftige Token-Anfragen laufen über (1)
 *   3) Fallback: kurzlebigen Access Token aus dem Credential direkt verwenden
 */
async function requestDriveTokenNative(interactive = true) {
  console.log('🔑 [Native] Fordere Drive Access Token an...');

  try {
    // 1. Versuche Server-seitigen Token (nutzt gespeicherten Refresh Token)
    console.log('🔑 [Native] Versuche Server-seitigen Drive Token...');
    try {
      const { getDriveToken } = await import('./apiService');
      const serverToken = await getDriveToken();
      if (serverToken) {
        const { setGoogleAccessToken } = await import('./googleAuthToken');
        setGoogleAccessToken(serverToken);
        console.log('🔑 [Native] ✅ Drive Token vom Server erhalten');
        return serverToken;
      }
    } catch (serverErr) {
      console.warn('🔑 [Native] Server-Token nicht verfügbar:', serverErr.message);
    }

    // 2. Falls kein Server-Token: Re-Auth mit Drive Scope
    if (!interactive) {
      throw new Error('Drive Token nicht verfügbar (non-interactive)');
    }

    console.log('🔑 [Native] Starte Re-Auth mit Drive Scope...');
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');

    const result = await FirebaseAuthentication.signInWithGoogle({
      scopes: ['https://www.googleapis.com/auth/drive.file'],
    });

    const { setGoogleAccessToken } = await import('./googleAuthToken');

    // 2a. serverAuthCode → Server tauscht ihn gegen Access + REFRESH Token.
    //     Danach kann der Server den Drive-Token selbst erneuern → kein
    //     erneutes Anmelden mehr nötig (das war das eigentliche Problem auf
    //     Android: Firebase liefert nie einen Refresh Token, nur kurzlebige
    //     Access Tokens).
    const authCode = result.credential?.serverAuthCode;
    if (authCode) {
      try {
        const { exchangeDriveCode } = await import('./apiService');
        const exchanged = await exchangeDriveCode(authCode, ''); // Android: leerer redirect_uri
        if (exchanged?.accessToken) {
          setGoogleAccessToken(exchanged.accessToken);
          console.log(
            '🔑 [Native] ✅ Drive Token via serverAuthCode (Refresh Token:',
            exchanged.hasRefreshToken ? '✅ dauerhaft verbunden' : '❌ nicht erhalten',
            ')'
          );
          return exchanged.accessToken;
        }
      } catch (e) {
        console.warn('🔑 [Native] serverAuthCode-Exchange fehlgeschlagen, nutze Access Token direkt:', e.message);
      }
    }

    // 2b. Fallback: Access Token aus dem Credential direkt (kurzlebig, ohne Refresh)
    if (result.credential?.accessToken) {
      setGoogleAccessToken(result.credential.accessToken);
      console.log('🔑 [Native] ✅ Drive Token via Re-Auth erhalten (ohne Refresh Token)');

      // Token auf Server cachen (non-blocking)
      try {
        const { storeDriveToken } = await import('./apiService');
        await storeDriveToken(result.credential.accessToken);
      } catch (e) {
        console.warn('🔑 [Native] Token-Server-Cache fehlgeschlagen:', e.message);
      }

      return result.credential.accessToken;
    }

    throw new Error('Kein Drive Token nach Re-Auth erhalten');

  } catch (error) {
    console.error('🔑 [Native] Drive Token fehlgeschlagen:', error);
    throw error;
  }
}

/**
 * Web Drive Token via GIS Authorization Code Flow
 * 
 * Nutzt initCodeClient statt initTokenClient, damit der Server
 * einen echten Refresh Token erhält und selbstständig erneuern kann.
 */
function requestDriveTokenWeb(interactive = true) {
  // Code-Flow unterstützt kein Silent — Server-Refresh übernimmt das
  if (!interactive) {
    return Promise.reject(new Error('Code-Flow benötigt Interaktion'));
  }

  return new Promise(async (resolve, reject) => {
    try {
      await ensureGISLoaded();
    } catch (err) {
      reject(err);
      return;
    }

    const codeClient = window.google.accounts.oauth2.initCodeClient({
      client_id: WEB_CLIENT_ID,
      scope: 'https://www.googleapis.com/auth/drive.file',
      ux_mode: 'popup',
      // ⚡ Bestehenden Access Token widerrufen damit Google neuen Consent zeigt
      // und einen frischen Refresh Token sendet (sonst kommt kein Refresh Token!)
      select_account: false,
      callback: async (response) => {
        if (response.error) {
          console.warn('🔑 Code-Flow Fehler:', response.error);
          reject(new Error(response.error_description || response.error));
          return;
        }

        try {
          // Code an Server senden → Exchange gegen Access + Refresh Token
          const { exchangeDriveCode } = await import('./apiService');
          const result = await exchangeDriveCode(response.code);

          if (result.accessToken) {
            const { setGoogleAccessToken } = await import('./googleAuthToken');
            setGoogleAccessToken(result.accessToken);
            console.log('🔑 ✅ Drive Token via Code-Exchange erhalten (Refresh Token:', result.hasRefreshToken ? '✅' : '❌', ')');
            resolve(result.accessToken);
          } else {
            reject(new Error('Kein Access Token nach Code-Exchange'));
          }
        } catch (err) {
          console.error('🔑 Code-Exchange fehlgeschlagen:', err);
          reject(err);
        }
      },
      error_callback: (err) => {
        console.warn('🔑 Code-Flow Error Callback:', err);
        reject(new Error(err.message || tGlobal('ui.svc.driveAuthFailed')));
      },
    });

    // Vor Code-Request: bestehenden Token widerrufen damit Google neuen Consent erzwingt → Refresh Token
    try {
      const existingToken = sessionStorage.getItem('_googleAccessToken');
      if (existingToken && window.google?.accounts?.oauth2?.revoke) {
        window.google.accounts.oauth2.revoke(existingToken, () => {
          console.log('🔑 Alter Drive Token widerrufen → erzwinge neuen Consent');
        });
      }
    } catch (e) { /* ignorieren */ }

    codeClient.requestCode();
  });
}

/**
 * Google Session widerrufen (Logout)
 */
export async function revokeGoogleSession() {
  try {
    // Native: Capacitor Firebase Auth
    if (isNativePlatform()) {
      try {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        await FirebaseAuthentication.signOut();
        console.log('🔐 [Native] Google Session beendet');
      } catch (e) {
        console.warn('🔐 [Native] Logout-Fehler:', e);
      }
      return;
    }
    
    // Web: GIS
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
    // Token widerrufen falls vorhanden
    if (window.google?.accounts?.oauth2) {
      try {
        window.google.accounts.oauth2.revoke(
          sessionStorage.getItem('_googleAccessToken') || '',
          () => console.log('🔐 Google Token widerrufen')
        );
      } catch (e) {
        // Ignorieren — Token war eh abgelaufen
      }
    }
  } catch (e) {
    console.warn('🔐 Fehler beim Google Session Revoke:', e);
  }
}

// ============================================================
// 📋 EXPORTS
// ============================================================

export { WEB_CLIENT_ID };
